import { EvidenceGateRollout } from "@/http/api-types";

// byRolloutDesc puts the most recent rollout first, matching the concluded table.
export function byRolloutDesc(rollouts: EvidenceGateRollout[]): EvidenceGateRollout[] {
  return [...rollouts].sort(
    (left, right) => right.rollout_at.localeCompare(left.rollout_at) || right.experiment_id - left.experiment_id,
  );
}

export type LiftTone = "win" | "neutral" | "worse" | "none";

// liftTone reads a realized lift against the gate threshold. Missing the bar while
// still improving is "neutral", so only an actual regression reads as red. A null lift
// is "none", never a miss — no traffic to measure is a different statement from
// measured and below the bar.
export function liftTone(lift: number | null, threshold: number): LiftTone {
  if (lift === null) return "none";
  if (lift >= threshold) return "win";
  return lift < 0 ? "worse" : "neutral";
}

const LIFT_TONE_CLASS: Record<LiftTone, string> = {
  win: "text-green-600 dark:text-green-400",
  neutral: "",
  worse: "text-red-600 dark:text-red-400",
  none: "text-muted-foreground",
};

export function liftToneClass(lift: number | null, threshold: number): string {
  return LIFT_TONE_CLASS[liftTone(lift, threshold)];
}

export function formatLift(lift: number | null): string {
  if (lift === null) return "no traffic";
  const rounded = Math.round(lift * 1000) / 10;
  return `${rounded > 0 ? "+" : ""}${rounded}%`;
}

export function formatEUR(value: number): string {
  return `€${Math.round(value).toLocaleString("en-US")}`;
}

// dailySpend lets a reader see the scale a figure was measured at. The baseline runs
// over the whole experiment, the post windows over seven days, so the totals are not
// comparable but the daily rates are.
export function dailySpend(totalEUR: number, days: number): number {
  if (days <= 0) return 0;
  return totalEUR / days;
}

export function inclusiveDays(from: string, to: string): number {
  const start = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const end = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return 0;
  return Math.round((end - start) / 86_400_000) + 1;
}
